import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TemplateMeta } from '../../api/templates';
import { TemplatePreviewDialog } from '../templates/TemplatePreviewDialog';
import { PreviewCard as MetaPreviewCard } from '../templates/MetaPreviewCard';
import { DEMO_CARD_USER } from './digitalCardStyles';
import { buildAuthSignupUrl, savePreSignupTemplate } from '../../utils/templateOnboarding';
import { markDigitalCardSignupSource } from '../../utils/signupRedirect';
import { getTemplatePreviewUrl } from './templatePreviewAssets';

type Props = {
  template: TemplateMeta;
  selected?: boolean;
  onSelect?: (template: TemplateMeta) => void;
  onUse?: (template: TemplateMeta) => void;
  showActions?: boolean;
  /** linktree = flat preview + name; default = card with action buttons */
  variant?: 'default' | 'linktree';
};

function persistAndGo(template: TemplateMeta, onUse?: (template: TemplateMeta) => void) {
  const category = String(template.category || '').toLowerCase().includes('artist') ? 'artist' : 'business';
  savePreSignupTemplate(
    {
      templateMetaId: template.id,
      componentKey: template.componentKey,
      category: category as 'business' | 'artist',
      name: template.name,
    },
    template
  );
  markDigitalCardSignupSource();
  if (onUse) {
    onUse(template);
    return;
  }
  window.location.href = buildAuthSignupUrl(template.id);
}

export const TemplateGalleryCard: React.FC<Props> = ({
  template,
  selected = false,
  onSelect,
  onUse,
  showActions = true,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewSrc = getTemplatePreviewUrl(template);

  const handleUse = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    persistAndGo(template, onUse);
  };

  const handleSelect = () => {
    onSelect?.(template);
  };

  if (variant === 'linktree') {
    return (
      <>
        <article
          className={`dcl-lt-card${selected ? ' dcl-lt-card--selected' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          aria-label={t('dcl_template_select_aria', { defaultValue: 'View details for {{name}}', name: template.name })}
          onClick={handleSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelect();
            }
          }}
        >
          <div className="dcl-lt-card-preview">
            {previewSrc ? (
              <div className="dcl-lt-card-thumb">
                <img src={previewSrc} alt={template.name} width={577} height={1024} loading="lazy" decoding="async" />
              </div>
            ) : (
              <div className="dcl-lt-card-fallback">{template.name}</div>
            )}
          </div>
          <h3 className="dcl-lt-card-name">{template.name}</h3>
        </article>

        <TemplatePreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} title={template.name}>
          <MetaPreviewCard template={template} user={DEMO_CARD_USER} mode="full" frame="phone" />
        </TemplatePreviewDialog>
      </>
    );
  }

  return (
    <>
      <article
        className={`dcl-template-card${selected ? ' dcl-template-card--selected' : ''}${showActions ? '' : ' dcl-template-card--browse'}`}
        role={showActions && onSelect ? 'button' : undefined}
        tabIndex={showActions && onSelect ? 0 : undefined}
        aria-pressed={showActions && onSelect ? selected : undefined}
        aria-label={
          showActions && onSelect
            ? t('dcl_template_select_aria', { defaultValue: 'View details for {{name}}', name: template.name })
            : undefined
        }
        onClick={showActions && onSelect ? handleSelect : undefined}
        onKeyDown={
          showActions && onSelect
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect();
                }
              }
            : undefined
        }
      >
        <div className="dcl-template-preview">
          {previewSrc ? (
            <img src={previewSrc} alt={template.name} loading="lazy" />
          ) : (
            <span style={{ color: '#94A3B8', fontSize: 14 }}>{template.name}</span>
          )}
          {selected && <span className="dcl-template-selected-badge">{t('dcl_template_selected', { defaultValue: 'Selected' })}</span>}
        </div>
        <div className="dcl-template-body">
          <h3 className="dcl-template-name">{template.name}</h3>
          <p className="dcl-template-cat">
            {String(template.category || '').toLowerCase().includes('artist')
              ? t('dcl_filter_artist', { defaultValue: 'Artist' })
              : t('dcl_filter_business', { defaultValue: 'Business' })}
          </p>
          {showActions && (
            <div className="dcl-template-actions">
              <button
                type="button"
                className="dcl-btn dcl-btn--ghost dcl-btn--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewOpen(true);
                }}
              >
                {t('dcl_template_preview', { defaultValue: 'Preview' })}
              </button>
              <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--sm" onClick={handleUse}>
                {t('dcl_template_use', { defaultValue: 'Use This Template' })}
              </button>
            </div>
          )}
        </div>
      </article>

      <TemplatePreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} title={template.name}>
        <MetaPreviewCard template={template} user={DEMO_CARD_USER} mode="full" frame="phone" />
      </TemplatePreviewDialog>
    </>
  );
};

export default TemplateGalleryCard;
